import { ApiError, authHeaders } from './auth'

const base = (import.meta.env.VITE_TASKS_API_BASE as string | undefined)?.replace(
  /\/$/,
  '',
) ?? ''

function raise(res: Response, label: string): never {
  if (res.status === 403) throw new ApiError(403, 'not_allowlisted')
  if (res.status === 401) throw new ApiError(401, 'unauthorized')
  throw new ApiError(res.status, `${label} ${res.status}`)
}

export type KnowledgeStatus = 'draft' | 'indexing' | 'active' | 'archived' | 'failed'

export type KnowledgeCorpus = {
  id: string
  org_id: string | null
  slug: string
  name: string
  status: KnowledgeStatus
  active_version: number | null
  metadata: Record<string, unknown>
  created_at: string | null
}

export type KnowledgeDocMeta = {
  doc_id: string
  slug: string
  title: string
  description: string
  status: KnowledgeStatus
  version: number
  source_kind: string
  line_count: number
  node_count: number
}

export type StructureNode = {
  node_id: string
  title: string
  level: number
  line_num?: number | null
  summary?: string
  text?: string
  nodes?: StructureNode[]
}

export type FetchedNode = {
  node_id: string
  title: string
  line_num: number | null
  summary: string
  content: string
}

export async function listCorpora(): Promise<KnowledgeCorpus[]> {
  const res = await fetch(`${base}/knowledge/corpora`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'list_corpora')
  return (await res.json()) as KnowledgeCorpus[]
}

export async function createCorpus(body: {
  slug: string
  name: string
}): Promise<KnowledgeCorpus> {
  const res = await fetch(`${base}/knowledge/corpora`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) raise(res, 'create_corpus')
  return (await res.json()) as KnowledgeCorpus
}

export async function listDocuments(
  corpusId: string,
  status?: KnowledgeStatus | 'all',
): Promise<KnowledgeDocMeta[]> {
  const q = new URLSearchParams()
  if (status && status !== 'all') q.set('status', status)
  const qs = q.toString()
  const res = await fetch(
    `${base}/knowledge/corpora/${corpusId}/documents${qs ? `?${qs}` : ''}`,
    { headers: await authHeaders() },
  )
  if (!res.ok) raise(res, 'list_documents')
  return (await res.json()) as KnowledgeDocMeta[]
}

export async function ingestMarkdown(body: {
  corpus_id: string
  slug: string
  markdown: string
  source_name?: string
  activate?: boolean
}): Promise<KnowledgeDocMeta> {
  const res = await fetch(`${base}/knowledge/documents/ingest-markdown`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) raise(res, 'ingest_markdown')
  return (await res.json()) as KnowledgeDocMeta
}

export async function getDocument(docId: string): Promise<KnowledgeDocMeta> {
  const res = await fetch(`${base}/knowledge/documents/${docId}`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'get_document')
  return (await res.json()) as KnowledgeDocMeta
}

export async function getStructure(docId: string): Promise<{
  doc_id: string
  structure: StructureNode[]
}> {
  const res = await fetch(`${base}/knowledge/documents/${docId}/structure`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'get_structure')
  return (await res.json()) as { doc_id: string; structure: StructureNode[] }
}

export async function fetchNodes(
  docId: string,
  selector: string,
): Promise<{ doc_id: string; nodes: FetchedNode[] }> {
  const res = await fetch(`${base}/knowledge/documents/${docId}/nodes`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ selector }),
  })
  if (!res.ok) raise(res, 'fetch_nodes')
  return (await res.json()) as { doc_id: string; nodes: FetchedNode[] }
}

export async function activateDocument(docId: string): Promise<KnowledgeDocMeta> {
  const res = await fetch(`${base}/knowledge/documents/${docId}/activate`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'activate')
  return (await res.json()) as KnowledgeDocMeta
}

export async function archiveDocument(docId: string): Promise<KnowledgeDocMeta> {
  const res = await fetch(`${base}/knowledge/documents/${docId}/archive`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'archive')
  return (await res.json()) as KnowledgeDocMeta
}
